import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Icon, Notifications, Text } from "@jds/core";
import { Box } from "@mui/material";
import { useParams } from "react-router-dom";
import EmptyState from "../components/NoData/EmptyState";
import PageLoader from "../components/UI/Loader/PageLoader";
import useApiHttp from "../hooks/use-http";
import "./Recommendation.css";

const PAGE_SIZE = 50;

const getClusterLabel = (cluster, prefix) => {
  const label =
    cluster?.targetClusterName ||
    cluster?.sourceClusterName ||
    cluster?.targetName ||
    cluster?.sourceName ||
    cluster?.username ||
    cluster?.name;

  if (label) {
    return label;
  }

  return `${prefix} ${cluster?.targetClusterId || cluster?.sourceClusterId || ""}`.trim();
};

const getPendingRecommendations = (cluster) =>
  (cluster?.recommendations || []).filter(
    (recommendation) =>
      !recommendation?.recommendationStatus ||
      recommendation?.recommendationStatus === "PENDING",
  );

const getPendingRecommendationCount = (cluster) =>
  getPendingRecommendations(cluster).length;

const getClusterKey = (cluster) =>
  `${cluster?.targetClusterId || "cluster"}-${cluster?.mediaUrl || "default"}`;

const SameIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" />
    <path
      d="M8.6 12.3l2.2 2.2 4.6-5.2"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
  </svg>
);

const DifferentIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" />
    <path
      d="M9 9l6 6M15 9l-6 6"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="2"
    />
  </svg>
);

const UnsureIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" />
    <path
      d="M10.2 9.4a2.2 2.2 0 114 1.2c0 1.5-1.7 2-2.2 2.9"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
    <circle cx="12" cy="16.9" r="1" fill="currentColor" />
  </svg>
);

const SuccessIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" />
    <path
      d="M8.6 12.3l2.2 2.2 4.6-5.2"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
  </svg>
);

const RecommendationCard = ({ cluster, remainingCount, onClick }) => {
  const label = getClusterLabel(cluster, "Cluster");
  const isUnknown = label.toLowerCase().includes("unknown");
  const isDisabled = remainingCount <= 0;

  return (
    <button
      className="recommendation-card"
      type="button"
      disabled={isDisabled}
      onClick={() => onClick(cluster)}
    >
      <div className="recommendation-card__avatar-shell">
        <img
          className="recommendation-card__avatar"
          src={cluster?.mediaUrl || ""}
          alt={label}
        />
      </div>
      <div className="recommendation-card__content">
        <p
          className={`recommendation-card__name ${isUnknown ? "recommendation-card__name--unknown" : ""
            }`}
        >
          {label}
        </p>
        <p className="recommendation-card__count">
          {remainingCount} new {remainingCount === 1 ? "match" : "matches"}
        </p>
      </div>
    </button>
  );
};

const ReviewActionCard = ({ label, icon, selected, variant, onClick }) => (
  <button
    className={`recommendation-modal__choice recommendation-modal__choice--${variant} ${selected ? "recommendation-modal__choice--selected" : ""
      }`}
    type="button"
    onClick={onClick}
  >
    <span
      className={`recommendation-modal__choice-icon recommendation-modal__choice-icon--${variant}`}
    >
      {icon}
    </span>
    <Text
      text={label}
      appearance="body-xs-bold"
      as="span"
      className="recommendation-modal__choice-text"
    />
  </button>
);

const ModalShell = ({ children }) => (
  <div className="recommendation-modal__backdrop" role="presentation">
    <div className="recommendation-modal">{children}</div>
  </div>
);

const RESPONSE_STATUS_MAP = {
  same: "MERGED",
  different: "REJECTED",
  "not-sure": "SKIPPED",
};

const ReviewModal = ({
  cluster,
  selectedRecommendation,
  selectedResponse,
  currentIndex,
  totalReviews,
  hasAnyResponse,
  onClose,
  onPrevious,
  onDone,
  onResponse,
}) => {
  if (!cluster || !selectedRecommendation) {
    return null;
  }

  const label = getClusterLabel(cluster, "Cluster");
  const progressWidth = totalReviews
    ? `${((currentIndex + 1) / totalReviews) * 100}%`
    : "0%";

  return (
    <ModalShell>
      <div
        className="recommendation-modal__content"
        role="dialog"
        aria-modal="true"
        aria-labelledby="recommendation-modal-title"
      >
        {/* <div className="recommendation-modal__close">
          <CloseButton onClick={onClose} />
        </div> */}

        <div className="recommendation-modal__header">
          <Text
            text="Same or different person?"
            appearance="body-l-bold"
            as="h2"
            id="recommendation-modal-title"
            className="recommendation-modal__title"
          />
          <Text
            text="JioAI has found some matches for you"
            appearance="body-xs"
            color="primary-grey-80"
            as="p"
            className="recommendation-modal__subtitle"
          />
        </div>

        <div className="recommendation-modal__comparison">
          <div className="recommendation-modal__face-wrap">
            <div className="recommendation-modal__face-shell">
              <img src={cluster?.mediaUrl || ""} alt={label} />
            </div>
          </div>
          <span className="recommendation-modal__arrow" aria-hidden="true">
            ↔
          </span>
          <div
            key={`${selectedRecommendation?.recommendationId || currentIndex}-${currentIndex}`}
            className="recommendation-modal__face-wrap recommendation-modal__face-wrap--animated"
          >
            <div className="recommendation-modal__face-shell">
              <img
                src={selectedRecommendation?.mediaUrl || ""}
                alt={getClusterLabel(selectedRecommendation, "Match")}
              />
            </div>
          </div>
        </div>

        <div className="recommendation-modal__choices">
          <ReviewActionCard
            label="Same"
            icon={<SameIcon />}
            variant="same"
            selected={selectedResponse === "same"}
            onClick={() => onResponse("same")}
          />
          <ReviewActionCard
            label="Different"
            icon={<DifferentIcon />}
            variant="different"
            selected={selectedResponse === "different"}
            onClick={() => onResponse("different")}
          />
          <ReviewActionCard
            label="Not sure"
            icon={<UnsureIcon />}
            variant="unsure"
            selected={selectedResponse === "not-sure"}
            onClick={() => onResponse("not-sure")}
          />
        </div>

        <div className="recommendation-modal__progress">
          <div className="recommendation-modal__progress-top">
            <Text
              text="Review progress"
              appearance="body-xs"
              color="primary-grey-60"
              as="span"
              className="recommendation-modal__progress-label"
            />
            <Text
              text={`${currentIndex + 1} / ${totalReviews}`}
              appearance="body-xs-bold"
              color="primary-50"
              as="span"
              className="recommendation-modal__progress-count"
            />
          </div>
          <div className="recommendation-modal__progress-bar">
            <div
              className="recommendation-modal__progress-value"
              style={{ width: progressWidth }}
            />
          </div>
        </div>

        <div className="recommendation-modal__footer recommendation-modal__footer--spread">
          <button
            className="recommendation-modal__footer-btn recommendation-modal__footer-btn--primary"
            type="button"
            onClick={onPrevious}
            disabled={currentIndex === 0}
          >
            <span className="recommendation-modal__footer-prefix" aria-hidden="true">
              <Icon ic="IcChevronLeft" color="primary_60" size="s" />
            </span>
            <Text
              text="Previous"
              appearance="body-xs-bold"
              as="span"
              className="recommendation-modal__footer-text"
              color="primary_50"
            />
          </button>
          <button
            className="recommendation-modal__footer-btn"
            type="button"
            onClick={onClose}
          >
            <Text
              text="Cancel"
              appearance="body-xs-bold"
              as="span"
              className="recommendation-modal__footer-text"
              color="primary_grey_80"
            />
          </button>
          <button
            className="recommendation-modal__footer-btn recommendation-modal__footer-btn--primary"
            type="button"
            onClick={onDone}
            disabled={!hasAnyResponse}
          >
            <Text
              text="Done"
              appearance="body-xs-bold"
              as="span"
              className="recommendation-modal__footer-text"
              color="primary_50"
            />
          </button>
        </div>
      </div>
    </ModalShell>
  );
};

const ConfirmationModal = ({ onConfirm, onCancel }) => (
  <ModalShell>
    <div
      className="recommendation-confirmation"
      role="dialog"
      aria-modal="true"
      aria-labelledby="recommendation-confirmation-title"
    >
      <div className="recommendation-confirmation__header">
        <Box className="recommendation-confirmation__text-wrap">
          <Text
            text="Are you sure you want to confirm the selections?"
            appearance="body-l-bold"
            as="h2"
            id="recommendation-confirmation-title"
            className="recommendation-confirmation__title"
          />
        </Box>
        <Box className="recommendation-confirmation__text-wrap">
          <Text
            text="The chosen photos will be moved to this face cluster."
            appearance="body-m"
            color="primary-grey-80"
            as="p"
            className="recommendation-confirmation__subtitle"
          />
        </Box>
      </div>

      <div className="recommendation-confirmation__actions">
        <button
          className="recommendation-button recommendation-button--primary"
          type="button"
          onClick={onConfirm}
        >
          OK
        </button>
        <button
          className="recommendation-button recommendation-button--secondary"
          type="button"
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </div>
  </ModalShell>
);

const CompletionModal = ({
  remainingCount,
  onContinue,
  onMaybeLater,
}) => (
  <ModalShell>
    <div
      className="recommendation-completion"
      role="dialog"
      aria-modal="true"
      aria-labelledby="recommendation-completion-title"
    >
      <div className="recommendation-completion__icon">
        <SuccessIcon />
      </div>
      <div className="recommendation-completion__header">
        <Box className="recommendation-completion__text-wrap">
          <Text
            text="Review Complete!"
            appearance="body-l-bold"
            as="h2"
            id="recommendation-completion-title"
            className="recommendation-completion__title"
          />
        </Box>
        <Box className="recommendation-completion__text-wrap">
          <Text
            text={remainingCount ? `You still have ${remainingCount} recommendation${remainingCount === 1 ? "" : "s"
              } left to review.` : 'No recommendations left to review'}
            appearance="body-m"
            color="primary-grey-80"
            as="p"
            className="recommendation-completion__subtitle"
          />
        </Box>
      </div>

      {remainingCount > 0 &&
        <Box className="recommendation-completion__text-wrap">
          <Text
            text="Would you like to keep going?"
            appearance="body-m"
            color="primary-grey-80"
            as="p"
            className="recommendation-completion__prompt"
          />
        </Box>}

      <div className="recommendation-completion__actions">
        {remainingCount > 0 &&
          <button
            className="recommendation-button recommendation-button--primary"
            type="button"
            onClick={onContinue}
            disabled={remainingCount <= 0}
          >
            Continue
          </button>
        }
        <button
          className="recommendation-button recommendation-button--secondary"
          type="button"
          onClick={onMaybeLater}
        >
          {remainingCount > 0 ? 'Maybe Later' : 'Close'}
        </button>
      </div>
    </div>
  </ModalShell>
);

export default function Recommendation() {
  const { id: eventId } = useParams();
  const [recommendationList, setRecommendationList] = useState([]);
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [currentReviewIndex, setCurrentReviewIndex] = useState(0);
  const [reviewResponses, setReviewResponses] = useState({});
  const [activeModal, setActiveModal] = useState(null);
  const [completionRemainingCount, setCompletionRemainingCount] = useState(0);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const { sendRequest: getRecommendationList, isLoading: recommendationListLoading } =
    useApiHttp();
  const { sendRequest: submitRecommendationReview, isLoading: submitRecommendationLoading } =
    useApiHttp();
  const observerRef = useRef(null);
  const skipNextPageFetchRef = useRef(false);

  const fetchRecommendationList = useCallback((pageNumber, onFetched = null) => {
    getRecommendationList(
      {
        url: `events/${eventId}/recommendation?page=${pageNumber}&size=${PAGE_SIZE}`,
        method: "GET",
        base: "MEDIA",
      },
      null,
      (data) => {
        const clusters = data?.data?.clusters || [];
        setRecommendationList((prev) =>
          pageNumber === 0 ? clusters : [...prev, ...clusters],
        );
        setHasMore(clusters.length === PAGE_SIZE);
        onFetched && onFetched(clusters);
      },
      () => { },
    );
  }, [eventId, getRecommendationList]);

  useEffect(() => {
    if (skipNextPageFetchRef.current) {
      skipNextPageFetchRef.current = false;
      return;
    }
    fetchRecommendationList(page);
  }, [fetchRecommendationList, page]);

  const lastElementRef = useCallback(
    (node) => {
      if (recommendationListLoading) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0]?.isIntersecting && hasMore) {
          setPage((prev) => prev + 1);
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [hasMore, recommendationListLoading],
  );

  const selectedRecommendations = useMemo(
    () => getPendingRecommendations(selectedCluster),
    [selectedCluster],
  );

  const selectedRecommendation = selectedRecommendations[currentReviewIndex];
  const totalReviews = selectedRecommendations.length;
  const remainingCount = completionRemainingCount;
  const hasAnyResponse = Object.keys(reviewResponses).length > 0;

  const handleCardClick = (cluster) => {
    const recommendationCount = cluster?.recommendations?.length || 0;
    const pendingRecommendationCount = getPendingRecommendationCount(cluster);

    if (!recommendationCount || !pendingRecommendationCount) {
      return;
    }

    setSelectedCluster(cluster);
    setCompletionRemainingCount(pendingRecommendationCount);
    setCurrentReviewIndex(0);
    setReviewResponses({});
    setActiveModal("review");
  };

  const resetSession = () => {
    setSelectedCluster(null);
    setCurrentReviewIndex(0);
    setReviewResponses({});
    setActiveModal(null);
  };

  const handlePrevious = () => {
    setCurrentReviewIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleResponse = (response) => {
    setReviewResponses((prev) => ({
      ...prev,
      [currentReviewIndex]: response,
    }));

    if (currentReviewIndex < totalReviews - 1) {
      setCurrentReviewIndex((prev) => prev + 1);
    }
  };

  const handleDone = () => {
    if (!hasAnyResponse) {
      return;
    }

    setActiveModal("confirm");
  };

  const handleConfirmSelections = () => {
    if (!selectedCluster) {
      return;
    }

    submitRecommendationReview(
      {
        url: `events/${eventId}/recommendation`,
        method: "POST",
        base: "MEDIA",
        body: {
          targetClusterId: selectedCluster?.targetClusterId,
          payloads: selectedRecommendations.map((recommendation, index) => ({
            sourceClusterId: recommendation?.sourceClusterId,
            status: RESPONSE_STATUS_MAP[reviewResponses[index]] || "PENDING",
          })),
        },
      },
      null,
      (data) => {
        const pendingCountFromResponse =
          data?.data?.find?.((item) => item?.recommendationStatus === "PENDING")
            ?.count ?? 0;

        Notifications.toast.success({
          id: "recommendation-review-success",
          title: data?.message || "Recommendation review updated",
        });

        skipNextPageFetchRef.current = true;
        setPage(0);
        fetchRecommendationList(0, (clusters) => {
          const refreshedCluster = clusters.find(
            (cluster) =>
              cluster?.targetClusterId === selectedCluster?.targetClusterId,
          );

          setSelectedCluster(refreshedCluster || null);
          setCompletionRemainingCount(
            pendingCountFromResponse ?? getPendingRecommendationCount(refreshedCluster),
          );
          setReviewResponses({});
          setCurrentReviewIndex(0);
          setActiveModal("complete");
        });
      },
      (err, data) => {
        Notifications.toast.error({
          id: "recommendation-review-error",
          title: data?.message || "Failed to update recommendation review",
        });
      },
    );
  };

  const handleContinue = () => {
    if (remainingCount <= 0) {
      resetSession();
      return;
    }

    if (getPendingRecommendationCount(selectedCluster) > 0) {
      setCurrentReviewIndex(0);
      setReviewResponses({});
    }

    setCompletionRemainingCount(getPendingRecommendationCount(selectedCluster));
    setActiveModal("review");
  };

  return (
    <>
      <Box className="recommendation-page">
        <div className="recommendation-page__header">
          <Text text="Recommendations" appearance="heading-xs" />
          <Text
            text="Help the AI improve its accuracy by reviewing these suggested matches."
            appearance="body-m-bold"
            color="primary-grey-80"
          />
        </div>

        {recommendationList.length ? (
          <div className="recommendation-page__grid">
            {recommendationList.map((cluster, index) => (
              <div
                key={getClusterKey(cluster)}
                ref={
                  hasMore && index === recommendationList.length - 1
                    ? lastElementRef
                    : null
                }
              >
                <RecommendationCard
                  cluster={cluster}
                  remainingCount={getPendingRecommendationCount(cluster)}
                  onClick={handleCardClick}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="recommendation-page__empty-state">
            <EmptyState
              title="No recommendation found"
              subtitle="Recommendations will appear here once matches are available for review."
              action={false}
            />
          </div>
        )}

        {recommendationListLoading && page > 0 && (
          <div className="recommendation-page__loading-more">
            <Text
              text="Loading more recommendations..."
              appearance="body-s"
              color="primary-grey-80"
            />
          </div>
        )}
      </Box>

      {activeModal === "review" && (
        <ReviewModal
          cluster={selectedCluster}
          selectedRecommendation={selectedRecommendation}
          selectedResponse={reviewResponses[currentReviewIndex]}
          currentIndex={currentReviewIndex}
          totalReviews={totalReviews}
          hasAnyResponse={hasAnyResponse}
          onClose={resetSession}
          onPrevious={handlePrevious}
          onDone={handleDone}
          onResponse={handleResponse}
        />
      )}

      {activeModal === "confirm" && (
        <ConfirmationModal
          onConfirm={handleConfirmSelections}
          onCancel={() => setActiveModal("review")}
        />
      )}

      {activeModal === "complete" && (
        <CompletionModal
          remainingCount={remainingCount}
          onContinue={handleContinue}
          onMaybeLater={resetSession}
        />
      )}

      {((recommendationListLoading && page === 0) || submitRecommendationLoading) && (
        <PageLoader open={(recommendationListLoading && page === 0) || submitRecommendationLoading} />
      )}
    </>
  );
}
